import { useState } from 'react';
import { KYC_ALLOWED_MIME_TYPES } from '../../lib/validators/kycSchema';

const MAX_FILES = 2;
const MAX_BYTES = 5 * 1024 * 1024;

function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Shared 1-2 file picker for anywhere the app collects KYC documents —
 * investor self-service submission and admin Onboard Client both take the
 * exact same `documents` shape (jpg/jpeg/png/pdf, ≤5MB each, max 2 files).
 * Validates client-side on selection (instant feedback) rather than only
 * at form submit, per the spec's "pre-check before hitting the API" ask.
 */
export default function KycFilesInput({ value = [], onChange, error, label = 'Documents (1–2 files)' }) {
  const [localError, setLocalError] = useState(null);

  const addFiles = (fileList) => {
    setLocalError(null);
    const incoming = Array.from(fileList ?? []);
    const accepted = [];

    for (const file of incoming) {
      if (value.length + accepted.length >= MAX_FILES) {
        setLocalError(`Only ${MAX_FILES} files allowed — remove one to add another.`);
        break;
      }
      if (!KYC_ALLOWED_MIME_TYPES.includes(file.type)) {
        setLocalError(`${file.name} isn't a JPG, PNG, or PDF.`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        setLocalError(`${file.name} is larger than 5MB.`);
        continue;
      }
      accepted.push(file);
    }

    if (accepted.length) onChange([...value, ...accepted]);
  };

  const removeAt = (idx) => {
    setLocalError(null);
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground-muted mb-1.5">{label}</label>

      {value.length > 0 && (
        <ul className="mb-2 space-y-1.5">
          {value.map((file, idx) => (
            <li
              key={`${file.name}-${idx}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background-elevated px-3 py-2 text-xs"
            >
              <span className="truncate text-foreground">
                {file.name} <span className="text-foreground-subtle">({formatBytes(file.size)})</span>
              </span>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="shrink-0 text-foreground-subtle hover:text-danger transition-colors"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {value.length < MAX_FILES && (
        <input
          type="file"
          accept="image/jpeg,image/png,image/jpg,application/pdf"
          multiple
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
          className="w-full text-sm text-foreground-muted file:mr-3 file:rounded-lg file:border-0 file:bg-background-elevated file:px-3 file:py-2 file:text-sm file:font-medium file:text-foreground file:cursor-pointer hover:file:bg-background-surface"
        />
      )}

      <p className="mt-1.5 text-xs text-foreground-subtle">JPG, PNG, or PDF. Up to 5MB each, max {MAX_FILES} files.</p>
      {(localError || error) && <p className="mt-1.5 text-xs text-danger">{localError || error}</p>}
    </div>
  );
}
